import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore'
import { db } from '../../lib/firebase'
import {
  UserPOIComment,
  CreateUserPOICommentData,
  UpdateUserPOICommentData
} from '../../types/userPoiComments'

const USER_POI_COMMENTS_COLLECTION = 'user_poi_comments'

/**
 * Create a new comment on a user POI
 */
export async function createUserPOIComment(
  commentData: CreateUserPOICommentData,
  authorId: string,
  authorName: string,
  authorPhoto?: string
): Promise<string> {
  const comment: any = {
    ...commentData,
    authorId,
    authorName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }

  // Only add optional fields if they have values
  if (authorPhoto) {
    comment.authorPhoto = authorPhoto
  }

  const docRef = await addDoc(collection(db, USER_POI_COMMENTS_COLLECTION), comment)
  return docRef.id
}

/**
 * Get all comments for a user POI
 */
export async function getUserPOIComments(poiId: string): Promise<UserPOIComment[]> {
  const q = query(
    collection(db, USER_POI_COMMENTS_COLLECTION),
    where('poiId', '==', poiId),
    orderBy('createdAt', 'asc')
  )

  const snapshot = await getDocs(q)
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as UserPOIComment))
}

/**
 * Subscribe to comments for a user POI
 */
export function subscribeToUserPOIComments(
  poiId: string,
  callback: (comments: UserPOIComment[]) => void
): Unsubscribe {
  const q = query(
    collection(db, USER_POI_COMMENTS_COLLECTION),
    where('poiId', '==', poiId),
    orderBy('createdAt', 'asc')
  )

  return onSnapshot(q, (snapshot) => {
    const comments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as UserPOIComment))
    
    callback(comments)
  })
}

/**
 * Update a user POI comment
 */
export async function updateUserPOIComment(
  commentId: string,
  updates: UpdateUserPOICommentData
): Promise<void> {
  const docRef = doc(db, USER_POI_COMMENTS_COLLECTION, commentId)
  
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  })
}

/**
 * Delete a user POI comment
 */
export async function deleteUserPOIComment(commentId: string): Promise<void> {
  await deleteDoc(doc(db, USER_POI_COMMENTS_COLLECTION, commentId))
}

/**
 * Get comment counts for multiple user POIs
 */
export async function getUserPOICommentCounts(poiIds: string[]): Promise<Record<string, number>> {
  if (poiIds.length === 0) {
    return {}
  }

  // Firebase has a limit of 10 items in an 'in' query, so we need to batch
  const counts: Record<string, number> = {}
  const batchSize = 10
  
  for (let i = 0; i < poiIds.length; i += batchSize) {
    const batch = poiIds.slice(i, i + batchSize)
    
    const q = query(
      collection(db, USER_POI_COMMENTS_COLLECTION),
      where('poiId', 'in', batch)
    )
    
    const snapshot = await getDocs(q)
    
    // Initialize counts for all POIs in this batch
    batch.forEach(poiId => {
      counts[poiId] = 0
    })
    
    // Count comments for each POI
    snapshot.docs.forEach(doc => {
      const data = doc.data()
      if (data.poiId) {
        counts[data.poiId] = (counts[data.poiId] || 0) + 1
      }
    })
  }
  
  return counts
}